/**
 * The render engine state machine shared by the worker and the in-thread
 * fallback: holds tile pixel arrays and summary colours, renders raster
 * tiles to PNG.
 */
import { encodeRGBAPNG } from '@drawearth/shared';
import type { FromWorker, ToWorker } from './protocol.ts';
import { renderFull, renderSummary, type SummaryLevel } from './raster.ts';

export class RenderEngine {
  private tiles = new Map<number, Uint8Array | null>();
  private summaries: Record<SummaryLevel, Map<number, number>> = { tile: new Map(), cell: new Map() };

  async handle(msg: ToWorker): Promise<FromWorker | null> {
    switch (msg.type) {
      case 'tile':
        this.tiles.set(msg.key, msg.pixels);
        return null;
      case 'diff': {
        let px = this.tiles.get(msg.key);
        if (!px) {
          px = new Uint8Array(65536);
          this.tiles.set(msg.key, px);
        }
        for (const e of msg.entries) px[e.idx] = e.colour;
        return null;
      }
      case 'summary':
        this.summaries[msg.level] = new Map(msg.entries);
        return null;
      case 'clearTiles':
        this.tiles.clear();
        return null;
      case 'render': {
        try {
          const provider = { get: (k: number) => this.tiles.get(k) };
          const rgba =
            msg.mode === 'full'
              ? renderFull(msg.z, msg.x, msg.y, msg.size, provider)
              : renderSummary(msg.z, msg.x, msg.y, msg.size, msg.mode, this.summaries[msg.mode]);
          const png = await encodeRGBAPNG(msg.size, msg.size, rgba);
          return { type: 'rendered', id: msg.id, png: png.buffer.slice(png.byteOffset, png.byteOffset + png.byteLength) as ArrayBuffer };
        } catch (err) {
          return { type: 'error', id: msg.id, message: err instanceof Error ? err.message : String(err) };
        }
      }
    }
  }

  /** Read back a tile (for hit-testing on the main-thread fallback). */
  getTile(key: number): Uint8Array | null | undefined {
    return this.tiles.get(key);
  }
}
